/*
 * CameraMode contains camera interaction modes for standalone deck.gl viewers.
 *
 * It keeps the default deck.gl map-like controller available and adds a local
 * orbit-style controller for small scenes rendered without a map background.
 *
 * User-facing orbit behavior:
 * - Plain left drag pans the scene in the current camera screen plane. Movement
 *   feels like dragging the rendered model itself, so the local camera offset is
 *   changed while the geographic view center stays stable.
 * - Ctrl/Cmd + left drag rotates the camera around the scene point that was
 *   under the pointer at drag start. Horizontal mouse movement changes bearing
 *   around that picked point. Vertical mouse movement changes pitch as a smooth
 *   vertical orbit, so the scene tilts around a stable visual anchor instead of
 *   sliding away or zooming toward the pointer.
 * - Wheel changes deck.gl zoom in orbit mode. WASD pans using physical keyboard
 *   keys, so it works independently from the active keyboard layout. Numpad +/-
 *   changes zoom in both modes.
 *
 * Implementation overview:
 * - Map mode delegates pointer camera interaction to deck.gl's built-in
 *   controller. Orbit mode disables the built-in controller and applies pointer,
 *   wheel, and keyboard updates directly through setViewState.
 * - An orbit drag captures a full start snapshot: view center, zoom, pitch,
 *   bearing, local position offset, pointer pixel, and a picked 3D rotation
 *   pivot. The picked pivot comes from deck.gl picking when possible, with a
 *   horizontal scene-plane unproject fallback.
 * - Vertical tilt follows Cesium's terrain-orbit idea. The click pivot remains
 *   the horizontal bearing center, while a separate vertical pivot is resolved
 *   at viewport center X and the original pointer Y. The real start camera-eye
 *   offset from that vertical pivot is saved; later pitch updates rotate that
 *   actual offset around the camera screen-right axis. This avoids reconstructing
 *   the camera from an artificial radius and prevents jumps when dragging starts.
 * - Bearing is applied after the pitch step by rotating the intermediate camera
 *   eye around the original click pivot in the local XY plane. The view is then
 *   recentered with WebMercatorViewport.panByPosition3D/panByPosition so the
 *   click pivot remains under the original pointer pixel.
 * - deck.gl exposes camera state as longitude/latitude/zoom/pitch/bearing plus
 *   a local position offset. Helper math below converts between those view-state
 *   fields, effective camera-eye coordinates, and local meter vectors.
 */
import {WebMercatorViewport} from "@deck.gl/core";
import {stripCameraTransition} from "@/engine/deck/CameraTransition.js";

export const CAMERA_MODE = Object.freeze({
    MAP: "map",
    ORBIT: "orbit",
});

const ORBIT_ROTATION_SPEED = 0.25;
const ORBIT_ZOOM_SPEED = 0.002;
const ORBIT_PAN_INVERT_Y = true;
// Converts vertical pointer movement as a viewport-height ratio into pitch angle.
const ORBIT_VERTICAL_ROTATION_SPEED = 1;
// Enables requestAnimationFrame interpolation for orbit wheel zoom instead of applying every wheel delta immediately.
const ORBIT_SMOOTH_WHEEL_ZOOM_ENABLED = true;
// Duration keeps wheel zoom responsive while avoiding a visible one-frame jump.
const ORBIT_SMOOTH_WHEEL_ZOOM_DURATION_MS = 160;
// Pixel tolerance used when trying to resolve a 3D rotation pivot from deck.gl picking.
const ORBIT_ROTATION_PICK_RADIUS = 4;

const CAMERA_KEYBOARD_WSAD_PAN_ENABLED = true;
const CAMERA_KEYBOARD_PAN_STEP_PIXELS = 24;
const CAMERA_KEYBOARD_ZOOM_ENABLED = true;
// Numpad +/- changes deck.gl zoom by this amount per key press.
const CAMERA_KEYBOARD_ZOOM_STEP = 0.35;
// Enables requestAnimationFrame interpolation for keyboard pan and zoom commands.
const CAMERA_KEYBOARD_SMOOTH_MOVE_ENABLED = true;
// Keyboard movement can be a little longer than wheel zoom because it happens in discrete steps.
const CAMERA_KEYBOARD_SMOOTH_MOVE_DURATION_MS = 180;

/**
 * Controls deck.gl camera interaction mode for a standalone 3D viewport.
 */
export class DeckCameraModeController {
    /**
     * Creates a controller that can switch deck.gl between map and orbit interactions.
     *
     * @param {object} options Controller dependencies.
     * @param {HTMLElement} options.container DOM element that receives pointer and wheel events.
     * @param {Deck} options.deck deck.gl instance whose controller option will be changed.
     * @param {Function} options.getViewState Returns the current deck.gl view state.
     * @param {Function} options.setViewState Applies a new deck.gl view state to the viewer.
     * @param {Function} [options.onModeChange] Called with the active mode after switching.
     * @returns {DeckCameraModeController} Initialized camera mode controller.
     * @throws {Error} Throws when required dependencies are missing.
     */
    constructor(options) {
        if (!options?.container || !options?.deck || !options?.getViewState || !options?.setViewState) {
            throw new Error("DeckCameraModeController requires container, deck, getViewState, and setViewState");
        }

        this.container = options.container;
        this.deck = options.deck;
        this.getViewState = options.getViewState;
        this.setViewState = options.setViewState;
        this.onModeChange = options.onModeChange;
        this.mode = CAMERA_MODE.MAP;
        this.wheelZoomFrame = null;
        this.wheelZoomAnimation = null;
        this.keyboardMoveFrame = null;
        this.keyboardMoveAnimation = null;
        this.dragState = null;
        this._onPointerDown = this._handlePointerDown.bind(this);
        this._onPointerMove = this._handlePointerMove.bind(this);
        this._onPointerUp = this._handlePointerUp.bind(this);
        this._onWheel = this._handleWheel.bind(this);
        this._onKeyDown = this._handleKeyDown.bind(this);

        this._bindEvents();
        this._applyDeckControllerMode();
    }

    /**
     * Returns the current camera interaction mode.
     *
     * @returns {string} Active mode, either "map" or "orbit".
     */
    getMode() {
        return this.mode;
    }

    /**
     * Switches the active camera interaction mode.
     *
     * @param {string} mode Requested mode, either CAMERA_MODE.MAP or CAMERA_MODE.ORBIT.
     * @returns {string} Applied camera mode.
     */
    setMode(mode) {
        const normalizedMode = normalizeCameraMode(mode);

        if (this.mode === normalizedMode) {
            return this.mode;
        }

        this.mode = normalizedMode;
        this.dragState = null;
        this._cancelSmoothWheelZoom();
        this._cancelSmoothKeyboardMove();
        this._applyDeckControllerMode();
        this.onModeChange?.(this.mode);

        return this.mode;
    }

    /**
     * Toggles between map and orbit camera interaction modes.
     *
     * @returns {string} Applied camera mode after toggling.
     */
    toggleMode() {
        return this.setMode(this.mode === CAMERA_MODE.ORBIT ? CAMERA_MODE.MAP : CAMERA_MODE.ORBIT);
    }

    /**
     * Releases DOM listeners owned by the camera mode controller.
     *
     * @returns {void}
     */
    destroy() {
        this._cancelSmoothWheelZoom();
        this._cancelSmoothKeyboardMove();
        this._unbindEvents();
        this.dragState = null;
    }

    /**
     * Registers pointer and wheel listeners used by orbit mode.
     *
     * @returns {void}
     */
    _bindEvents() {
        this.container.addEventListener("pointerdown", this._onPointerDown);
        this.container.addEventListener("pointermove", this._onPointerMove);
        this.container.addEventListener("pointerup", this._onPointerUp);
        this.container.addEventListener("pointercancel", this._onPointerUp);
        this.container.addEventListener("wheel", this._onWheel, {passive: false});
        window.addEventListener("keydown", this._onKeyDown);
    }

    /**
     * Removes pointer and wheel listeners owned by this controller.
     *
     * @returns {void}
     */
    _unbindEvents() {
        this.container.removeEventListener("pointerdown", this._onPointerDown);
        this.container.removeEventListener("pointermove", this._onPointerMove);
        this.container.removeEventListener("pointerup", this._onPointerUp);
        this.container.removeEventListener("pointercancel", this._onPointerUp);
        this.container.removeEventListener("wheel", this._onWheel);
        window.removeEventListener("keydown", this._onKeyDown);
    }

    /**
     * Enables deck.gl's default controller only for map mode.
     *
     * @returns {void}
     */
    _applyDeckControllerMode() {
        this.deck.setProps({
            controller: this.mode === CAMERA_MODE.MAP ?
                true :
                Object.freeze({
                    scrollZoom: false,
                    dragPan: false,
                    dragRotate: false,
                    doubleClickZoom: false,
                    touchZoom: false,
                    touchRotate: false,
                    keyboard: false,
                }), // Disable deck.gl's controller custom handlers realted to user input.
        });
    }

    /**
     * Starts or retargets a smooth wheel-zoom animation.
     *
     * @param {object} current Current deck.gl view state.
     * @param {number} targetZoom Target zoom level after applying the wheel delta.
     * @returns {void}
     */
    _startSmoothWheelZoom(current, targetZoom) {
        const startZoom = Number.isFinite(current.zoom) ? current.zoom : targetZoom;

        this.wheelZoomAnimation = {
            startZoom,
            targetZoom,
            startedAt: performance.now(),
        };

        if (this.wheelZoomFrame === null) {
            this.wheelZoomFrame = requestAnimationFrame(() => this._stepSmoothWheelZoom());
        }
    }

    /**
     * Advances the active smooth wheel-zoom animation.
     *
     * @returns {void}
     */
    _stepSmoothWheelZoom() {
        this.wheelZoomFrame = null;

        const animation = this.wheelZoomAnimation;

        if (!animation) {
            return;
        }

        const elapsed = performance.now() - animation.startedAt;
        const progress = clamp(elapsed / ORBIT_SMOOTH_WHEEL_ZOOM_DURATION_MS, 0, 1);
        const zoom = interpolateNumber(animation.startZoom, animation.targetZoom, easeOutCubic(progress));
        const latest = stripCameraTransition(this.getViewState()) || {};

        this.setViewState({
            ...latest,
            zoom,
        });

        if (progress < 1) {
            this.wheelZoomFrame = requestAnimationFrame(() => this._stepSmoothWheelZoom());
            return;
        }

        this.wheelZoomAnimation = null;
    }

    /**
     * Cancels any active smooth wheel-zoom animation.
     *
     * @returns {void}
     */
    _cancelSmoothWheelZoom() {
        if (this.wheelZoomFrame !== null) {
            cancelAnimationFrame(this.wheelZoomFrame);
            this.wheelZoomFrame = null;
        }

        this.wheelZoomAnimation = null;
    }

    /**
     * Starts or retargets a smooth keyboard camera movement.
     *
     * @param {object} targetViewState Target view state after one keyboard command.
     * @returns {void}
     */
    _startSmoothKeyboardMove(targetViewState) {
        const current = stripCameraTransition(this.getViewState()) || targetViewState;

        this.keyboardMoveAnimation = {
            startViewState: current,
            targetViewState: stripCameraTransition(targetViewState) || targetViewState,
            startedAt: performance.now(),
        };

        if (this.keyboardMoveFrame === null) {
            this.keyboardMoveFrame = requestAnimationFrame(() => this._stepSmoothKeyboardMove());
        }
    }

    /**
     * Advances the active smooth keyboard movement.
     *
     * @returns {void}
     */
    _stepSmoothKeyboardMove() {
        this.keyboardMoveFrame = null;

        const animation = this.keyboardMoveAnimation;

        if (!animation) {
            return;
        }

        const elapsed = performance.now() - animation.startedAt;
        const progress = clamp(elapsed / CAMERA_KEYBOARD_SMOOTH_MOVE_DURATION_MS, 0, 1);
        const eased = easeOutCubic(progress);
        const latest = stripCameraTransition(this.getViewState()) || animation.startViewState;

        this.setViewState({
            ...latest,
            longitude: interpolateOptionalNumber(animation.startViewState.longitude, animation.targetViewState.longitude, eased, latest.longitude),
            latitude: interpolateOptionalNumber(animation.startViewState.latitude, animation.targetViewState.latitude, eased, latest.latitude),
            zoom: interpolateOptionalNumber(animation.startViewState.zoom, animation.targetViewState.zoom, eased, latest.zoom),
            position: interpolateOptionalVector(animation.startViewState.position, animation.targetViewState.position, eased, latest.position),
        });

        if (progress < 1) {
            this.keyboardMoveFrame = requestAnimationFrame(() => this._stepSmoothKeyboardMove());
            return;
        }

        this.keyboardMoveAnimation = null;
    }

    /**
     * Cancels any active smooth keyboard camera movement.
     *
     * @returns {void}
     */
    _cancelSmoothKeyboardMove() {
        if (this.keyboardMoveFrame !== null) {
            cancelAnimationFrame(this.keyboardMoveFrame);
            this.keyboardMoveFrame = null;
        }

        this.keyboardMoveAnimation = null;
    }

    /**
     * Starts orbit-mode camera movement when the primary pointer button is pressed.
     *
     * @param {PointerEvent} event Browser pointer event.
     * @returns {void}
     */
    _handlePointerDown(event) {
        if (this.mode !== CAMERA_MODE.ORBIT || event.button !== 0) {
            return;
        }

        const viewState = this.getViewState() || {};
        const rect = this.container?.getBoundingClientRect?.();
        const localPoint = {
            x: event.clientX - (rect?.left || 0),
            y: event.clientY - (rect?.top || 0),
        };
        const dragMode = event.ctrlKey || event.metaKey ? "rotate" : "pan";

        // Capture both orbit pivots at pointer down. They must stay fixed for the
        // whole gesture, otherwise pitch/bearing would drift as the camera moves.
        const rotatePivot = dragMode === "rotate"
            ? pickOrbitPivot(this.deck, localPoint) || unprojectOrbitPivot(this.container, viewState, localPoint)
            : null;
        const rotateVerticalPivot = dragMode === "rotate"
            ? orbitVerticalPivot(this.container, viewState, localPoint, rotatePivot)
            : null;
        const rotateVerticalCameraOffset = dragMode === "rotate"
            ? orbitCameraOffsetFromPivot(this.container, viewState, rotateVerticalPivot)
            : null;

        this._cancelSmoothWheelZoom();
        this._cancelSmoothKeyboardMove();
        this.dragState = {
            mode: dragMode,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startLocalX: localPoint.x,
            startLocalY: localPoint.y,
            startLongitude: viewState.longitude || 0,
            startLatitude: viewState.latitude || 0,
            startZoom: viewState.zoom || 0,
            startPitch: viewState.pitch || 0,
            startBearing: viewState.bearing || 0,
            startPosition: normalizePosition(viewState.position),
            startRotatePivot: rotatePivot,
            startRotateVerticalPivot: rotateVerticalPivot,
            startRotateVerticalCameraOffset: rotateVerticalCameraOffset,
        };
        this.container.setPointerCapture?.(event.pointerId);
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * Applies camera-relative pan or Ctrl-drag rotation while orbit mode drag is active.
     *
     * @param {PointerEvent} event Browser pointer event.
     * @returns {void}
     */
    _handlePointerMove(event) {
        if (this.mode !== CAMERA_MODE.ORBIT || !this.dragState || event.pointerId !== this.dragState.pointerId) {
            return;
        }

        const current = this.getViewState() || {};
        const dx = event.clientX - this.dragState.startX;
        const dy = event.clientY - this.dragState.startY;
        const nextViewState = this.dragState.mode === "pan"
            ? this._panViewStateFromSnapshot(current, dx, dy, this.dragState)
            : this._rotateViewState(current, dx, dy);

        this.setViewState(nextViewState);
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * Finishes the active orbit drag operation.
     *
     * @param {PointerEvent} event Browser pointer event.
     * @returns {void}
     */
    _handlePointerUp(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) {
            return;
        }

        this.container.releasePointerCapture?.(event.pointerId);
        this.dragState = null;
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * Applies wheel zoom in orbit mode.
     *
     * @param {WheelEvent} event Browser wheel event.
     * @returns {void}
     */
    _handleWheel(event) {
        if (this.mode !== CAMERA_MODE.ORBIT) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const current = this.getViewState() || {};
        const minZoom = current.minZoom ?? 0;
        const maxZoom = current.maxZoom ?? 30;
        const baseZoom = Number.isFinite(this.wheelZoomAnimation?.targetZoom)
            ? this.wheelZoomAnimation.targetZoom
            : current.zoom || 0;
        const zoom = clamp(baseZoom - event.deltaY * ORBIT_ZOOM_SPEED, minZoom, maxZoom);

        this._cancelSmoothKeyboardMove();
        if (ORBIT_SMOOTH_WHEEL_ZOOM_ENABLED) {
            this._startSmoothWheelZoom(current, zoom);
            return;
        }

        const nextViewState = {
            ...current,
            zoom,
        };

        this.setViewState(nextViewState);
    }

    /**
     * Applies WASD camera panning in both camera modes.
     *
     * @param {KeyboardEvent} event Browser keyboard event.
     * @returns {void}
     */
    _handleKeyDown(event) {
        if (shouldIgnoreKeyboardEvent(event)) {
            return;
        }

        // Use physical key codes instead of localized characters so shortcuts
        // keep working with non-Latin keyboard layouts.
        let zoomDelta = null;

        if (CAMERA_KEYBOARD_ZOOM_ENABLED) {
            switch (event.code) {
                case "NumpadAdd":
                    zoomDelta = CAMERA_KEYBOARD_ZOOM_STEP;
                    break;
                case "NumpadSubtract":
                    zoomDelta = -CAMERA_KEYBOARD_ZOOM_STEP;
                    break;
                default:
                    break;
            }
        }

        if (zoomDelta !== null) {
            const current = this.keyboardMoveAnimation?.targetViewState || stripCameraTransition(this.getViewState()) || {};
            const minZoom = current.minZoom ?? 0;
            const maxZoom = current.maxZoom ?? 30;
            const nextViewState = {
                ...current,
                zoom: clamp((current.zoom || 0) + zoomDelta, minZoom, maxZoom),
            };

            this._cancelSmoothWheelZoom();
            if (CAMERA_KEYBOARD_SMOOTH_MOVE_ENABLED) {
                this._startSmoothKeyboardMove(nextViewState);
            } else {
                this._cancelSmoothKeyboardMove();
                this.setViewState(nextViewState);
            }
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        let delta = null;

        if (CAMERA_KEYBOARD_WSAD_PAN_ENABLED) {
            switch (event.code) {
                case "KeyA":
                    delta = {dx: CAMERA_KEYBOARD_PAN_STEP_PIXELS, dy: 0};
                    break;
                case "KeyD":
                    delta = {dx: -CAMERA_KEYBOARD_PAN_STEP_PIXELS, dy: 0};
                    break;
                case "KeyW":
                    delta = {dx: 0, dy: CAMERA_KEYBOARD_PAN_STEP_PIXELS};
                    break;
                case "KeyS":
                    delta = {dx: 0, dy: -CAMERA_KEYBOARD_PAN_STEP_PIXELS};
                    break;
                default:
                    break;
            }
        }

        if (!delta) {
            return;
        }

        const current = this.keyboardMoveAnimation?.targetViewState || this.getViewState() || {};
        const nextViewState = this.mode === CAMERA_MODE.MAP
            ? this._panMapViewStateFromCenter(current, delta.dx, delta.dy)
            // Orbit keyboard pan is calculated from the current target view so
            // repeated key presses accumulate from the in-flight animation end.
            : this._panViewStateFromSnapshot(current, delta.dx, delta.dy, {
                startLongitude: current.longitude || 0,
                startLatitude: current.latitude || 0,
                startZoom: current.zoom || 0,
                startPitch: current.pitch || 0,
                startBearing: current.bearing || 0,
                startPosition: normalizePosition(current.position),
            });

        this._cancelSmoothWheelZoom();
        if (CAMERA_KEYBOARD_SMOOTH_MOVE_ENABLED) {
            this._startSmoothKeyboardMove(nextViewState);
        } else {
            this._cancelSmoothKeyboardMove();
            this.setViewState(nextViewState);
        }
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * Moves the map-mode view center using deck.gl's WebMercator pan calculation.
     *
     * @param {object} current Current deck.gl view state.
     * @param {number} dx Horizontal screen-space movement in pixels.
     * @param {number} dy Vertical screen-space movement in pixels.
     * @returns {object} Updated view state with recalculated longitude and latitude.
     */
    _panMapViewStateFromCenter(current, dx, dy) {
        const viewport = createOrbitViewport(this.container, current);

        if (!viewport) {
            return current;
        }

        const centerPixel = [viewport.width / 2, viewport.height / 2];
        const centerLngLat = viewport.unproject(centerPixel);
        const nextCenter = viewport.panByPosition(centerLngLat, [
            centerPixel[0] + dx,
            centerPixel[1] + dy,
        ]);

        return {
            ...current,
            ...nextCenter,
        };
    }

    /**
     * Rotates around Cesium-like tilt and bearing pivots captured at pointer down.
     *
     * @param {object} current Current deck.gl view state.
     * @param {number} dx Horizontal pointer movement in pixels.
     * @param {number} dy Vertical pointer movement in pixels.
     * @returns {object} Updated view state with bearing, pitch, and pivot correction.
     */
    _rotateViewState(current, dx, dy) {
        // Convert raw pointer movement into deck.gl orientation fields first.
        // Bearing is linear in horizontal pixels; pitch is based on viewport
        // height so its sensitivity does not depend on where the user clicked.
        const pivot = this.dragState.startRotatePivot;
        const rotation = {
            bearing: this.dragState.startBearing + dx * ORBIT_ROTATION_SPEED,
            pitch: this._getOrbitPitch(current, dy),
        };

        if (!pivot) {
            // Without a stable 3D pivot we can still rotate the camera angles,
            // but we cannot keep a clicked scene point visually anchored.
            return {
                ...current,
                ...rotation,
            };
        }

        const verticalPivot = this.dragState.startRotateVerticalPivot || pivot;
        const verticalCameraOffset = this.dragState.startRotateVerticalCameraOffset;

        if (!Array.isArray(verticalCameraOffset)
            || verticalCameraOffset.length < 3
            || !Number.isFinite(verticalCameraOffset[0])
            || !Number.isFinite(verticalCameraOffset[1])
            || !Number.isFinite(verticalCameraOffset[2])) {
            // If the camera-eye offset could not be captured, keep at least the
            // click pivot under the cursor after applying orientation changes.
            return this._centerPivotAtStartPointer({
                ...current,
                longitude: this.dragState.startLongitude,
                latitude: this.dragState.startLatitude,
                zoom: this.dragState.startZoom,
                position: this.dragState.startPosition,
                ...rotation,
            }, pivot);
        }

        let viewState = {
            ...current,
            longitude: this.dragState.startLongitude,
            latitude: this.dragState.startLatitude,
            zoom: this.dragState.startZoom,
            position: this.dragState.startPosition,
            ...rotation,
        };
        const pitchDelta = rotation.pitch - this.dragState.startPitch;
        const pitchAxis = cameraScreenBasis(this.dragState.startBearing, this.dragState.startPitch).right;

        // Pitch is not rebuilt from radius/pitch fields. We rotate the real
        // camera-eye offset captured at drag start, which preserves continuity
        // at dy=0 and gives the same orbit radius the user started with.
        const tiltedCameraOffset = rotateVectorAroundAxis(
            verticalCameraOffset,
            pitchAxis,
            pitchDelta * Math.PI / 180
        );

        for (let iteration = 0; iteration < 3; iteration += 1) {
            // Rebuild local meter coordinates from the candidate view state on
            // every pass because withCameraPosition and recentering affect the
            // view center used by WebMercatorViewport.projectPosition.
            const viewport = createOrbitViewport(this.container, viewState);
            const pivotPosition = viewport ? coordinateLocalMeters(viewport, viewState, pivot) : null;
            const verticalPivotPosition = viewport ? coordinateLocalMeters(viewport, viewState, verticalPivot) : null;

            if (!pivotPosition || !verticalPivotPosition) {
                return viewState;
            }

            // First apply vertical tilt around the Cesium-style vertical pivot,
            // then apply bearing around the original clicked pivot.
            const verticalCameraPosition = [
                verticalPivotPosition[0] + tiltedCameraOffset[0],
                verticalPivotPosition[1] + tiltedCameraOffset[1],
                verticalPivotPosition[2] + tiltedCameraOffset[2],
            ];
            const targetCameraPosition = rotateLocalPointAroundZ(
                verticalCameraPosition,
                pivotPosition,
                -(rotation.bearing - this.dragState.startBearing)
            );

            // deck.gl stores local camera movement in viewState.position rather
            // than as an explicit camera eye, so convert the desired eye back to
            // the position offset and then recenter the click pivot on screen.
            viewState = withCameraPosition(this.container, viewState, targetCameraPosition);
            viewState = this._centerPivotAtStartPointer(viewState, pivot);
        }

        return viewState;
    }

    /**
     * Calculates pitch from vertical drag as a fraction of viewport height.
     *
     * @param {object} current Current deck.gl view state.
     * @param {number} dy Vertical pointer movement in pixels.
     * @returns {number} Pitch angle in degrees.
     */
    _getOrbitPitch(current, dy) {
        const size = viewportSize(this.container);
        const height = size ? Math.max(1, size.height) : 1;
        const pitchDelta = (-dy / height) * 180 * ORBIT_VERTICAL_ROTATION_SPEED;

        return clamp(this.dragState.startPitch + pitchDelta, current.minPitch ?? 0, current.maxPitch ?? 85);
    }

    /**
     * Recenters the view so the stored rotation pivot remains under the original pointer pixel.
     *
     * @param {object} viewState Candidate deck.gl view state.
     * @param {number[]} pivot Pivot coordinate [lng, lat, z].
     * @returns {object} View state with adjusted longitude and latitude when the viewport can be built.
     */
    _centerPivotAtStartPointer(viewState, pivot) {
        const viewport = createOrbitViewport(this.container, viewState);
        const panMethod = typeof viewport?.panByPosition3D === "function" ? "panByPosition3D" : "panByPosition";
        const center = viewport?.[panMethod]?.(pivot, [
            this.dragState.startLocalX,
            this.dragState.startLocalY,
        ]);

        return center ? {...viewState, ...center} : viewState;
    }

    /**
     * Moves camera position from a provided camera snapshot in the screen plane.
     *
     * @param {object} current Current deck.gl view state.
     * @param {number} dx Horizontal screen-space movement in pixels.
     * @param {number} dy Vertical screen-space movement in pixels.
     * @param {object} snapshot Camera state used as the movement origin.
     * @returns {object} Updated view state with camera-relative position offset.
     */
    _panViewStateFromSnapshot(current, dx, dy, snapshot) {
        if (!snapshot) {
            return current;
        }

        // Pan uses the start zoom to keep a single drag gesture linear. If we
        // used the continuously updated view state, meters-per-pixel could vary
        // during an animation and make keyboard/pointer pan feel elastic.
        const viewport = createOrbitViewport(this.container, {
            longitude: snapshot.startLongitude,
            latitude: snapshot.startLatitude,
            zoom: snapshot.startZoom,
        });

        if (!viewport) {
            return current;
        }

        // Convert screen pixels into local meters along the camera's current
        // right/up axes. Orbit pan intentionally changes only local position:
        // longitude/latitude remain the stable geographic anchor for debug,
        // serialization, and deck.gl projection.
        const basis = cameraScreenBasis(snapshot.startBearing, snapshot.startPitch);
        const metersX = -dx * viewport.metersPerPixel;
        const metersY = (ORBIT_PAN_INVERT_Y ? dy : -dy) * viewport.metersPerPixel;
        const position = [
            snapshot.startPosition[0] + basis.right[0] * metersX + basis.up[0] * metersY,
            snapshot.startPosition[1] + basis.right[1] * metersX + basis.up[1] * metersY,
            snapshot.startPosition[2] + basis.right[2] * metersX + basis.up[2] * metersY,
        ];

        return {
            ...current,
            position,
        };
    }

}

/**
 * Normalizes external camera mode input to a supported mode value.
 *
 * @param {string} mode Candidate camera mode.
 * @returns {string} Supported camera mode.
 */
export function normalizeCameraMode(mode) {
    return mode === CAMERA_MODE.ORBIT ? CAMERA_MODE.ORBIT : CAMERA_MODE.MAP;
}

/**
 * Checks whether keyboard shortcuts should ignore the current event target.
 *
 * @param {KeyboardEvent} event Browser keyboard event.
 * @returns {boolean} True when the user is typing into an editable control.
 */
function shouldIgnoreKeyboardEvent(event) {
    const target = event.target;
    const tagName = target?.tagName?.toLowerCase();

    return Boolean(
        event.defaultPrevented
        || event.altKey
        || event.ctrlKey
        || event.metaKey
        || tagName === "input"
        || tagName === "textarea"
        || tagName === "select"
        || target?.isContentEditable
    );
}

/**
 * Clamps a numeric value to the inclusive min/max range.
 *
 * @param {number} value Numeric value to clamp.
 * @param {number} min Minimum allowed value.
 * @param {number} max Maximum allowed value.
 * @returns {number} Clamped value.
 */
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

/**
 * Linearly interpolates between two numeric values.
 *
 * @param {number} start Start value.
 * @param {number} end End value.
 * @param {number} progress Normalized interpolation progress from 0 to 1.
 * @returns {number} Interpolated value.
 */
function interpolateNumber(start, end, progress) {
    return start + (end - start) * progress;
}

/**
 * Interpolates optional numeric view-state fields.
 *
 * @param {number|null|undefined} start Start value.
 * @param {number|null|undefined} end End value.
 * @param {number} progress Normalized interpolation progress from 0 to 1.
 * @param {number|null|undefined} fallback Value returned when interpolation is unavailable.
 * @returns {number|null|undefined} Interpolated or fallback value.
 */
function interpolateOptionalNumber(start, end, progress, fallback) {
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return fallback;
    }

    return interpolateNumber(start, end, progress);
}

/**
 * Interpolates optional three-component vector view-state fields.
 *
 * @param {number[]|null|undefined} start Start vector.
 * @param {number[]|null|undefined} end End vector.
 * @param {number} progress Normalized interpolation progress from 0 to 1.
 * @param {number[]|null|undefined} fallback Value returned when interpolation is unavailable.
 * @returns {number[]|null|undefined} Interpolated or fallback vector.
 */
function interpolateOptionalVector(start, end, progress, fallback) {
    if (!Array.isArray(start) || !Array.isArray(end)) {
        return fallback;
    }

    return [
        interpolateOptionalNumber(start[0], end[0], progress, fallback?.[0]),
        interpolateOptionalNumber(start[1], end[1], progress, fallback?.[1]),
        interpolateOptionalNumber(start[2], end[2], progress, fallback?.[2]),
    ];
}

/**
 * Applies an ease-out curve to smooth wheel zoom without adding lag at the end.
 *
 * @param {number} progress Normalized animation progress from 0 to 1.
 * @returns {number} Eased progress.
 */
function easeOutCubic(progress) {
    return 1 - Math.pow(1 - progress, 3);
}

/**
 * Reads the current DOM viewport size for geospatial panning calculations.
 *
 * @param {HTMLElement} container Renderer container element.
 * @returns {{width: number, height: number}|null} Positive viewport size, or null.
 */
function viewportSize(container) {
    const rect = container?.getBoundingClientRect?.();
    const width = Math.round(rect?.width || 0);
    const height = Math.round(rect?.height || 0);

    if (width <= 0 || height <= 0) {
        return null;
    }

    return {width, height};
}

/**
 * Creates a WebMercatorViewport for current orbit camera calculations.
 *
 * @param {HTMLElement} container Renderer container element.
 * @param {object} viewState Current or snapshot deck.gl view state.
 * @returns {WebMercatorViewport|null} Viewport instance, or null when size is unavailable.
 */
function createOrbitViewport(container, viewState) {
    const size = viewportSize(container);

    if (!size) {
        return null;
    }

    return new WebMercatorViewport({
        width: size.width,
        height: size.height,
        ...viewState,
    });
}

/**
 * Calculates the effective deck.gl camera eye position in local meters from the view center.
 *
 * @param {HTMLElement} container Renderer container element.
 * @param {object} viewState Current deck.gl view state.
 * @returns {number[]|null} Camera eye position [x, y, z] in local meters, or null when unavailable.
 */
function cameraPositionLocalMeters(container, viewState) {
    const viewport = createOrbitViewport(container, viewState);
    const cameraPosition = viewport?.cameraPosition;

    if (!viewport || !Array.isArray(cameraPosition)) {
        return null;
    }

    // WebMercatorViewport.cameraPosition is in projected world units. Subtract
    // the projected view center and divide by deck.gl's local units-per-meter
    // scales to get a stable local meter vector.
    const center = viewport.projectPosition([
        viewState.longitude || 0,
        viewState.latitude || 0,
        0,
    ]);
    const unitsPerMeter = viewport.distanceScales?.unitsPerMeter || [1, 1, 1];

    return cameraPosition.map((value, index) => {
        const scale = unitsPerMeter[index] || 1;

        return (value - center[index]) / scale;
    });
}

/**
 * Resolves the Cesium-style vertical tilt pivot at the viewport center X and original pointer Y.
 *
 * @param {HTMLElement} container Renderer container element.
 * @param {object} viewState Current deck.gl view state.
 * @param {{x: number, y: number}} localPoint Pointer coordinates relative to the viewport.
 * @param {number[]|null} pivot Picked rotation pivot coordinate [lng, lat, z].
 * @returns {number[]|null} Vertical tilt pivot coordinate [lng, lat, z], or null when unavailable.
 */
function orbitVerticalPivot(container, viewState, localPoint, pivot) {
    const viewport = createOrbitViewport(container, viewState);
    const size = viewportSize(container);

    if (!viewport || !size) {
        return validCoordinate(pivot) ? pivot : null;
    }

    // Cesium's terrain tilt separates the clicked bearing center from the
    // vertical tilt center. Using viewport center X at the original pointer Y
    // keeps vertical tilt balanced when the user starts near the top or bottom
    // of the screen.
    const coordinate = viewport.unproject([
        size.width / 2,
        localPoint.y,
    ], {
        targetZ: validCoordinate(pivot) ? pivot[2] : 0,
    });

    return validCoordinate(coordinate) ? coordinate : pivot;
}

/**
 * Calculates the effective camera-eye offset from a pivot coordinate in local meters.
 *
 * @param {HTMLElement} container Renderer container element.
 * @param {object} viewState Current deck.gl view state.
 * @param {number[]|null} pivot Pivot coordinate [lng, lat, z].
 * @returns {number[]|null} Offset vector [x, y, z] in local meters, or null when unavailable.
 */
function orbitCameraOffsetFromPivot(container, viewState, pivot) {
    const viewport = createOrbitViewport(container, viewState);
    const cameraPosition = cameraPositionLocalMeters(container, viewState);
    const pivotPosition = viewport ? coordinateLocalMeters(viewport, viewState, pivot) : null;

    if (!cameraPosition || !pivotPosition) {
        return null;
    }

    return [
        cameraPosition[0] - pivotPosition[0],
        cameraPosition[1] - pivotPosition[1],
        cameraPosition[2] - pivotPosition[2]
    ];
}

/**
 * Returns a view state whose local offset places the effective camera eye at the requested local position.
 *
 * @param {HTMLElement} container Renderer container element.
 * @param {object} viewState Candidate deck.gl view state.
 * @param {number[]} targetCameraPosition Desired camera eye [x, y, z] in local meters.
 * @returns {object} View state with adjusted position when the camera offset can be resolved.
 */
function withCameraPosition(container, viewState, targetCameraPosition) {
    // deck.gl does not expose an explicit camera-eye setter for this view state.
    // Build the same view with zero local offset, measure that effective eye,
    // and store the difference as viewState.position.
    const zeroOffsetCameraPosition = cameraPositionLocalMeters(container, {
        ...viewState,
        position: [0, 0, 0],
    });

    if (!zeroOffsetCameraPosition) {
        return viewState;
    }

    return {
        ...viewState,
        position: [
            targetCameraPosition[0] - zeroOffsetCameraPosition[0],
            targetCameraPosition[1] - zeroOffsetCameraPosition[1],
            targetCameraPosition[2] - zeroOffsetCameraPosition[2],
        ],
    };
}

/**
 * Converts a lng/lat/z coordinate into local meters from the current view center.
 *
 * @param {WebMercatorViewport} viewport Viewport built from the candidate view state.
 * @param {object} viewState Candidate deck.gl view state.
 * @param {number[]|null} coordinate Coordinate [lng, lat, z].
 * @returns {number[]|null} Local meter coordinate, or null when input is invalid.
 */
function coordinateLocalMeters(viewport, viewState, coordinate) {
    if (!viewport || !validCoordinate(coordinate)) {
        return null;
    }

    // Project both points through the same viewport so longitude/latitude
    // distortion, zoom, pitch, and bearing are handled by deck.gl. The final
    // conversion to meters mirrors cameraPositionLocalMeters.
    const center = viewport.projectPosition([
        viewState.longitude || 0,
        viewState.latitude || 0,
        0,
    ]);
    const projected = viewport.projectPosition(coordinate);
    const unitsPerMeter = viewport.distanceScales?.unitsPerMeter || [1, 1, 1];

    return projected.map((value, index) => {
        const scale = unitsPerMeter[index] || 1;

        return (value - center[index]) / scale;
    });
}

/**
 * Uses deck.gl picking to resolve a 3D coordinate under the orbit-rotate pointer.
 *
 * @param {Deck} deck deck.gl instance.
 * @param {{x: number, y: number}} localPoint Pointer coordinates relative to the viewport.
 * @returns {number[]|null} Picked coordinate [lng, lat, z], or null when picking is unavailable.
 */
function pickOrbitPivot(deck, localPoint) {
    if (!deck || typeof deck.pickObject !== "function") {
        return null;
    }

    try {
        const info = deck.pickObject({
            x: localPoint.x,
            y: localPoint.y,
            radius: ORBIT_ROTATION_PICK_RADIUS,
            unproject3D: true,
        });

        return validCoordinate(info?.coordinate) ? info.coordinate : null;
    } catch (error) {
        return null;
    }
}

/**
 * Falls back to a horizontal scene-plane pivot under the pointer.
 *
 * @param {HTMLElement} container Renderer container element.
 * @param {object} viewState Current deck.gl view state.
 * @param {{x: number, y: number}} localPoint Pointer coordinates relative to the viewport.
 * @returns {number[]|null} Unprojected coordinate [lng, lat, z], or null when unavailable.
 */
function unprojectOrbitPivot(container, viewState, localPoint) {
    const viewport = createOrbitViewport(container, viewState);
    const localOffset = normalizePosition(viewState?.position);
    const targetZ = Number.isFinite(localOffset[2]) ? localOffset[2] : 0;
    const coordinate = viewport?.unproject([localPoint.x, localPoint.y], {targetZ});

    return validCoordinate(coordinate) ? coordinate : null;
}

/**
 * Checks whether a picked or unprojected coordinate can be used as a rotation pivot.
 *
 * @param {number[]|null|undefined} coordinate Candidate coordinate.
 * @returns {boolean} True when longitude, latitude, and height are finite numbers.
 */
function validCoordinate(coordinate) {
    return Array.isArray(coordinate)
        && coordinate.length >= 3
        && Number.isFinite(coordinate[0])
        && Number.isFinite(coordinate[1])
        && Number.isFinite(coordinate[2]);
}

/**
 * Normalizes deck.gl position offsets to a mutable three-component vector.
 *
 * @param {number[]|null|undefined} position Current view-state position offset.
 * @returns {number[]} Position vector [x, y, z] in meters.
 */
function normalizePosition(position) {
    return [
        Number.isFinite(position?.[0]) ? position[0] : 0,
        Number.isFinite(position?.[1]) ? position[1] : 0,
        Number.isFinite(position?.[2]) ? position[2] : 0,
    ];
}

/**
 * Builds camera screen-plane basis vectors in local east/north/up meters.
 *
 * @param {number} bearing Camera bearing in degrees.
 * @param {number} pitch Camera pitch in degrees.
 * @returns {{right: number[], up: number[]}} Normalized screen right and screen up vectors.
 */
function cameraScreenBasis(bearing, pitch) {
    const bearingRad = degreesToRadians(bearing || 0);
    const pitchRad = degreesToRadians(pitch || 0);
    const right = normalizeVector([
        Math.cos(bearingRad),
        -Math.sin(bearingRad),
        0,
    ]);
    const forward = normalizeVector([
        Math.sin(bearingRad),
        Math.cos(bearingRad),
        0,
    ]);
    const up = normalizeVector([
        forward[0] * Math.cos(pitchRad),
        forward[1] * Math.cos(pitchRad),
        Math.sin(pitchRad),
    ]);

    return {right, up};
}

/**
 * Rotates a local point around a local pivot in the horizontal XY plane.
 *
 * @param {number[]} point Source [x, y, z] point.
 * @param {number[]} pivot Rotation pivot [x, y, z].
 * @param {number} angleDegrees Rotation angle in degrees.
 * @returns {number[]} Rotated [x, y, z] point.
 */
function rotateLocalPointAroundZ(point, pivot, angleDegrees) {
    const angle = degreesToRadians(angleDegrees);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const x = point[0] - pivot[0];
    const y = point[1] - pivot[1];

    return [
        pivot[0] + x * cos - y * sin,
        pivot[1] + x * sin + y * cos,
        point[2],
    ];
}

/**
 * Rotates a local vector around a normalized local axis.
 *
 * @param {number[]} vector Source [x, y, z] vector.
 * @param {number[]} axis Rotation axis [x, y, z].
 * @param {number} angle Rotation angle in radians.
 * @returns {number[]} Rotated [x, y, z] vector.
 */
function rotateVectorAroundAxis(vector, axis, angle) {
    const normalizedAxis = normalizeVector(axis);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const dot = vector[0] * normalizedAxis[0] + vector[1] * normalizedAxis[1] + vector[2] * normalizedAxis[2];
    const cross = [
        normalizedAxis[1] * vector[2] - normalizedAxis[2] * vector[1],
        normalizedAxis[2] * vector[0] - normalizedAxis[0] * vector[2],
        normalizedAxis[0] * vector[1] - normalizedAxis[1] * vector[0],
    ];

    return [
        vector[0] * cos + cross[0] * sin + normalizedAxis[0] * dot * (1 - cos),
        vector[1] * cos + cross[1] * sin + normalizedAxis[1] * dot * (1 - cos),
        vector[2] * cos + cross[2] * sin + normalizedAxis[2] * dot * (1 - cos),
    ];
}

/**
 * Converts degrees to radians.
 *
 * @param {number} degrees Angle in degrees.
 * @returns {number} Angle in radians.
 */
function degreesToRadians(degrees) {
    return degrees * Math.PI / 180;
}

/**
 * Normalizes a three-component vector.
 *
 * @param {number[]} vector Source vector.
 * @returns {number[]} Normalized vector, or zero vector when length is invalid.
 */
function normalizeVector(vector) {
    const length = Math.hypot(vector[0], vector[1], vector[2]);

    if (!Number.isFinite(length) || length === 0) {
        return [0, 0, 0];
    }

    return [
        vector[0] / length,
        vector[1] / length,
        vector[2] / length,
    ];
}
